import { Alert } from '@mui/material';
import { t } from 'i18next';
import { useCallback, useEffect, useState } from 'react';
import { InlineLoader } from '../../common/components/InlineLoader';
import { Dialog, DialogBody, DialogButtons } from '../../common/components/Modal/Modal';
import { getErrorMessage } from '../../common/errors/errors';
import { PrimaryButton, TertiaryButton } from '../components/Buttons';
import { FormSummary } from '../components/FormSummary';
import { SlotSelectionForm } from '../components/SlotSelectionForm';
import { TraceForm } from '../components/TraceForm';
import { Report } from '../models/Report';
import { Slot } from '../models/Slot';
import { SlotType } from '../models/SlotType';
import { SlotService } from '../services/SlotService';

enum Steps {
  SELECT_SLOT = 1,
  SLOT_CREATED = 2,
  UPLOAD_TRACE = 3,
  REPORT_CREATED = 4,
}

type Props = {
  projectId: number;
  slotId?: number;
  slotTypes: SlotType[];
  onClosed: (dismissed: boolean, slotId: number | undefined, reportId: number | undefined) => void;
};

export const UploadTraceFlow = ({ projectId, slotId, slotTypes, onClosed }: Props) => {
  // It's not a preference to have services in states, but we use it here so
  // that we can leverage same service in multiple functionalities.
  const [slotService] = useState<SlotService>(new SlotService());

  const [step, setStep] = useState<Steps>(Steps.SELECT_SLOT);
  const [slots, setSlots] = useState<Slot[] | null>(null);
  const [selectedSlot, setSelectedSlot] = useState<Slot | null>(null);
  const [report, setReport] = useState<Report | null>(null);
  const [hasNewSlot, setHasNewSlot] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchSlots = async () => {
      if (!projectId) return;
      try {
        const slots = await slotService.getProjectSlots(projectId);
        setSlots(slots);
      } catch (e: unknown) {
        setError(getErrorMessage(e));
      }
    };

    fetchSlots();

    // Cancel all active requests on projectId change or unmount
    return () => slotService.cancelAllRequests();
  }, [projectId, slotService]);

  const onSlotSelected = useCallback(
    (id: number) => {
      setSelectedSlot(slots?.find((s) => s.getId() === id) || null);
      setStep(Steps.UPLOAD_TRACE);
    },
    [slots]
  );

  useEffect(() => {
    if (slotId && !!slots) {
      onSlotSelected(slotId);
    }
  }, [slots, slotId, onSlotSelected]);

  const onSlotCreated = (s: Slot) => {
    setSelectedSlot(s);
    setHasNewSlot(true);
    setStep(Steps.SLOT_CREATED);
  };

  const onUploadTrace = () => {
    setStep(Steps.UPLOAD_TRACE);
  };

  const onReportCreated = useCallback((r: Report) => {
    setReport(r);
    setStep(Steps.REPORT_CREATED);
  }, []);

  const STEP_MAP = {
    [Steps.SELECT_SLOT]: {
      title: t('uploadTrace.selectSlot.title'),
      subtitle: t('uploadTrace.selectSlot.subtitle'),
      component: () => {
        if (error) {
          return (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          );
        }
        if (!slots) return <InlineLoader />;
        return (
          <SlotSelectionForm
            slots={slots}
            submit={{ label: t('uploadTrace.selectSlot.select'), action: onSlotSelected }}
            createSlot={{
              label: t('uploadTrace.selectSlot.create'),
              action: (name, description, slotType, tags) => slotService.createSlot(projectId, name, description, slotType, tags).then(onSlotCreated),
            }}
            back={{ label: t('uploadTrace.selectSlot.back'), action: () => onClosed(false, undefined, undefined) }}
            slotTypes={slotTypes}
          />
        );
      },
      actions: null,
    },
    [Steps.SLOT_CREATED]: {
      title: t('uploadTrace.slotCreated.title', { slotName: selectedSlot?.getName() }),
      subtitle: t('uploadTrace.slotCreated.subtitle'),
      component: () => <FormSummary slot={selectedSlot} />,
      actions: () => (
        <DialogButtons>
          <PrimaryButton autoFocus onClick={onUploadTrace}>
            {t('uploadTrace.slotCreated.submit')}
          </PrimaryButton>
        </DialogButtons>
      ),
    },
    [Steps.UPLOAD_TRACE]: {
      title: t('uploadTrace.upload.title'),
      subtitle: t('uploadTrace.upload.subtitle'),
      component: () => (
        <TraceForm projectId={projectId} slotId={selectedSlot!.getId()} showCapacityWarning={selectedSlot?.isAtCapacity()} onDone={onReportCreated} />
      ),
      actions: null,
    },
    [Steps.REPORT_CREATED]: {
      title: t('uploadTrace.uploadDone.title', { reportName: report?.getName() }),
      subtitle: t('uploadTrace.uploadDone.subtitle'),
      component: () => <FormSummary slot={selectedSlot} report={report} />,
      actions: () => (
        <DialogButtons>
          <TertiaryButton onClick={() => onClosed(true, selectedSlot?.getId(), report?.getId())}>{t('uploadTrace.uploadDone.back')}</TertiaryButton>
          <PrimaryButton autoFocus onClick={() => onClosed(false, selectedSlot?.getId(), report?.getId())}>
            {t('uploadTrace.uploadDone.submit')}
          </PrimaryButton>
        </DialogButtons>
      ),
    },
  };

  return (
    <Dialog
      title={STEP_MAP[step].title}
      subtitle={STEP_MAP[step].subtitle}
      onDismiss={() => onClosed(true, hasNewSlot || report?.getId() ? selectedSlot?.getId() : undefined, report?.getId())}
    >
      <DialogBody>{STEP_MAP[step].component()}</DialogBody>
      {STEP_MAP[step].actions?.()}
    </Dialog>
  );
};
